import { useState } from 'react';

/**
 * Helper to transform camelCase, PascalCase, or snake_case keys into Title Case labels.
 * E.g., 'hotelCode' -> 'Hotel Code', 'check_in_date' -> 'Check In Date', 'bookingId' -> 'Booking ID'
 */
const formatKeyLabel = (key) => {
  if (!key) return '';
  // Specific common abbreviations
  const upperCases = ['id', 'ip', 'uri', 'url', 'api', 'http', 'jwt', 'otp', 'csv', 'db'];
  
  return key
    .replace(/([A-Z])/g, ' $1') // Insert space before uppercase letters
    .replace(/[_-]/g, ' ')       // Replace underscores and hyphens with spaces
    .trim()
    .split(/\s+/)
    .map((word) => {
      const lower = word.toLowerCase();
      if (upperCases.includes(lower)) {
        return lower.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

/**
 * Checks if a string is a standard ISO timestamp.
 */
const isIsoDateString = (val) => {
  if (typeof val !== 'string' || val.length < 10) return false;
  return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/.test(val);
};

/**
 * Format date string into readable Indian format
 */
const formatDateTime = (val) => {
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    return d.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch {
    return val;
  }
};

/**
 * ActivityMetadataViewer
 * Formatted, interactive viewer for activity log metadata payloads.
 * Supports:
 * - Structured formatted table view (Default)
 * - Raw JSON view with Copy to Clipboard
 * - In-place search/filter
 * - Nested objects, arrays, status badges, formatted currency/numbers, and dates
 */
const ActivityMetadataViewer = ({ metadata, theme = 'purple', title = 'Event Metadata' }) => {
  const [viewMode, setViewMode] = useState('formatted'); // 'formatted' | 'json'
  const [copied, setCopied] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');

  if (!metadata || (typeof metadata === 'object' && Object.keys(metadata).length === 0)) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-center">
        <div className="text-gray-400 text-sm flex items-center justify-center gap-2">
          <i className="fas fa-info-circle text-gray-400"></i>
          <span>No additional metadata payload captured for this activity.</span>
        </div>
      </div>
    );
  }

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(metadata, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy metadata JSON:', err);
    }
  };

  const handleCopyValue = async (key, val) => {
    try {
      const text = typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val);
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
    } catch (err) {
      console.error('Failed to copy value:', err);
    }
  };

  // Render individual values with context-aware styling
  const renderValue = (val, keyName, depth = 0) => {
    if (val === null || val === undefined) {
      return <span className="text-gray-400 italic text-xs">null</span>;
    }

    if (typeof val === 'boolean') {
      return val ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">
          <i className="fas fa-check-circle text-[10px] text-green-600"></i> True
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-rose-100 text-rose-800">
          <i className="fas fa-times-circle text-[10px] text-rose-600"></i> False
        </span>
      );
    }

    if (typeof val === 'number') {
      const keyLower = (keyName || '').toLowerCase();
      const isMonetary = keyLower.includes('amount') || keyLower.includes('price') || keyLower.includes('total') ||
                         keyLower.includes('balance') || keyLower.includes('fee') || keyLower.includes('cost');
      return (
        <span className={`font-mono text-xs font-semibold ${isMonetary ? 'text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded' : 'text-indigo-700'}`}>
          {isMonetary ? `₹ ${val.toLocaleString('en-IN')}` : val.toLocaleString()}
        </span>
      );
    }

    if (typeof val === 'string') {
      // Check for ISO Date
      if (isIsoDateString(val)) {
        return (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-700 bg-gray-100 px-2 py-0.5 rounded font-mono">
            <i className="far fa-calendar-alt text-gray-500"></i>
            {formatDateTime(val)}
          </span>
        );
      }

      // Check for common status codes
      const upper = val.toUpperCase();
      if (['SUCCESS', 'COMPLETED', 'CONFIRMED', 'PAID', 'ACTIVE', 'RESOLVED', 'VERIFIED'].includes(upper)) {
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">
            <i className="fas fa-circle text-[6px] mr-1 text-green-600"></i> {val}
          </span>
        );
      }
      if (['FAILED', 'CANCELLED', 'REJECTED', 'ERROR', 'DISABLED', 'BLOCKED', 'CLOSED'].includes(upper)) {
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-rose-100 text-rose-800">
            <i className="fas fa-circle text-[6px] mr-1 text-rose-600"></i> {val}
          </span>
        );
      }
      if (['PENDING', 'PROCESSING', 'INITIATED', 'OPEN', 'IN_PROGRESS', 'HOLD'].includes(upper)) {
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">
            <i className="fas fa-circle text-[6px] mr-1 text-amber-600"></i> {val}
          </span>
        );
      }

      // Check for IP address
      if (/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(val)) {
        return <span className="font-mono text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-100">{val}</span>;
      }

      // Check for Trace / UUID / long identifiers
      if (/^[0-9a-fA-F-]{20,}$/.test(val) || /^[A-Za-z0-9_-]{24,}$/.test(val)) {
        return <span className="font-mono text-xs text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">{val}</span>;
      }

      return <span className="text-xs text-gray-800 break-words">{val}</span>;
    }

    if (Array.isArray(val)) {
      if (val.length === 0) {
        return <span className="text-gray-400 italic text-xs">Empty array []</span>;
      }

      const allPrimitives = val.every((item) => typeof item !== 'object' || item === null);
      if (allPrimitives) {
        return (
          <div className="flex flex-wrap gap-1">
            {val.map((item, idx) => (
              <span key={idx} className="bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded font-mono">
                {String(item)}
              </span>
            ))}
          </div>
        );
      }

      // Array of objects
      return (
        <div className="space-y-2 mt-1 w-full">
          {val.map((item, idx) => (
            <div key={idx} className="bg-white rounded-lg border border-gray-200 p-2.5 shadow-sm text-xs">
              <div className="text-[11px] font-semibold text-gray-500 mb-1.5 flex items-center gap-1">
                <span className="w-4 h-4 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-[10px]">
                  {idx + 1}
                </span>
                Item #{idx + 1}
              </div>
              {renderValue(item, `${keyName}_${idx}`, depth + 1)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof val === 'object') {
      const keys = Object.keys(val);
      if (keys.length === 0) {
        return <span className="text-gray-400 italic text-xs">Empty object {}</span>;
      }

      return (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-2.5 space-y-2 w-full mt-1">
          {keys.map((subKey) => (
            <div key={subKey} className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 pb-1.5 border-b border-gray-100 last:border-0 last:pb-0">
              <div className="text-xs font-medium text-gray-600 shrink-0 sm:w-1/3">
                {formatKeyLabel(subKey)}
                <span className="block font-mono text-[10px] text-gray-400 font-normal">{subKey}</span>
              </div>
              <div className="sm:w-2/3 break-words">
                {renderValue(val[subKey], subKey, depth + 1)}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-xs text-gray-800">{String(val)}</span>;
  };

  // Theme styling configurations
  const themeClasses = {
    purple: {
      badge: 'bg-purple-100 text-purple-800',
      activeTab: 'bg-purple-600 text-white shadow-sm',
      inactiveTab: 'text-gray-600 hover:text-purple-600 hover:bg-purple-50',
      accent: 'text-purple-600',
      border: 'focus:ring-purple-500 focus:border-purple-500'
    },
    emerald: {
      badge: 'bg-emerald-100 text-emerald-800',
      activeTab: 'bg-emerald-600 text-white shadow-sm',
      inactiveTab: 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50',
      accent: 'text-emerald-600',
      border: 'focus:ring-emerald-500 focus:border-emerald-500'
    },
    blue: {
      badge: 'bg-blue-100 text-blue-800',
      activeTab: 'bg-blue-600 text-white shadow-sm',
      inactiveTab: 'text-gray-600 hover:text-blue-600 hover:bg-blue-50',
      accent: 'text-blue-600',
      border: 'focus:ring-blue-500 focus:border-blue-500'
    }
  }[theme] || {
    badge: 'bg-purple-100 text-purple-800',
    activeTab: 'bg-purple-600 text-white shadow-sm',
    inactiveTab: 'text-gray-600 hover:text-purple-600 hover:bg-purple-50',
    accent: 'text-purple-600',
    border: 'focus:ring-purple-500 focus:border-purple-500'
  };

  const metadataKeys = Object.keys(metadata);
  const filteredKeys = metadataKeys.filter((k) => {
    if (!searchFilter.trim()) return true;
    const term = searchFilter.toLowerCase();
    const keyMatch = k.toLowerCase().includes(term) || formatKeyLabel(k).toLowerCase().includes(term);
    const valString = typeof metadata[k] === 'object' ? JSON.stringify(metadata[k]).toLowerCase() : String(metadata[k]).toLowerCase();
    return keyMatch || valString.includes(term);
  });

  return (
    <div className="space-y-3">
      {/* Header bar: Title, Count, Tabs & Copy */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
            <i className={`fas fa-code-branch ${themeClasses.accent}`}></i>
            {title}
          </h4>
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${themeClasses.badge}`}>
            {metadataKeys.length} {metadataKeys.length === 1 ? 'entry' : 'entries'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="inline-flex rounded-lg bg-gray-100 p-0.5 border border-gray-200 text-xs">
            <button
              type="button"
              onClick={() => setViewMode('formatted')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-medium transition-all ${
                viewMode === 'formatted' ? themeClasses.activeTab : themeClasses.inactiveTab
              }`}
            >
              <i className="fas fa-table-cells text-[11px]"></i> Formatted
            </button>
            <button
              type="button"
              onClick={() => setViewMode('json')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-medium transition-all ${
                viewMode === 'json' ? themeClasses.activeTab : themeClasses.inactiveTab
              }`}
            >
              <i className="fas fa-code text-[11px]"></i> Raw JSON
            </button>
          </div>

          {/* Copy JSON Button */}
          <button
            type="button"
            onClick={handleCopyJson}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm transition-all"
            title="Copy metadata as JSON"
          >
            <i className={`fas ${copied ? 'fa-check text-green-600' : 'fa-copy text-gray-500'}`}></i>
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>
      </div>

      {/* Formatted Structured View */}
      {viewMode === 'formatted' ? (
        <div className="space-y-2">
          {/* In-place search when more than 3 keys */}
          {metadataKeys.length > 3 && (
            <div className="relative mb-2">
              <input
                type="text"
                placeholder="Filter metadata fields..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className={`w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg bg-gray-50 focus:bg-white ${themeClasses.border}`}
              />
              <i className="fas fa-search absolute left-2.5 top-2 text-gray-400 text-xs"></i>
              {searchFilter && (
                <button
                  type="button"
                  onClick={() => setSearchFilter('')}
                  className="absolute right-2.5 top-1.5 text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              )}
            </div>
          )}

          {filteredKeys.length === 0 ? (
            <div className="py-6 text-center text-gray-400 text-xs bg-gray-50 rounded-xl border border-gray-200">
              No metadata keys match "{searchFilter}"
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm divide-y divide-gray-100">
              {filteredKeys.map((key) => {
                const val = metadata[key];
                return (
                  <div
                    key={key}
                    className="p-3 hover:bg-gray-50/80 transition-colors flex flex-col md:flex-row md:items-start justify-between gap-3 group"
                  >
                    {/* Key Column */}
                    <div className="md:w-1/3 shrink-0 flex items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-semibold text-gray-900">{formatKeyLabel(key)}</div>
                        <div className="text-[10px] font-mono text-gray-400 tracking-wide">{key}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCopyValue(key, val)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-700 rounded transition-opacity"
                        title="Copy field value"
                      >
                        <i className={`fas ${copiedKey === key ? 'fa-check text-green-600 text-[10px]' : 'fa-copy text-[10px]'}`}></i>
                      </button>
                    </div>

                    {/* Value Column */}
                    <div className="md:w-2/3 flex items-center justify-between gap-2 overflow-hidden">
                      <div className="w-full">{renderValue(val, key)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* Raw JSON View */
        <div className="relative">
          <pre className="bg-gray-900 text-emerald-400 p-4 rounded-xl text-xs overflow-x-auto font-mono max-h-72 border border-gray-800 shadow-inner">
            {JSON.stringify(metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default ActivityMetadataViewer;
